import { useState, useEffect, useRef } from 'react';
import {
  AdminLocationInfo, AdditionalAuthority, LocationResource,
  CalendarMethod, ScrapeType, updateLocation,
} from '../api/adminApi';
import styles from './CountryDrawer.module.css';

import { flagEmoji } from '../utils/flag';
import { resolveTimeZone } from '../utils/timezone';

function useLocalTime(timezone: string): string {
  const fmt = () => new Date().toLocaleTimeString('en-GB', { timeZone: resolveTimeZone(timezone), hour: '2-digit', minute: '2-digit' });
  const [t, setT] = useState(fmt);
  useEffect(() => {
    const id = setInterval(() => setT(fmt()), 30_000);
    return () => clearInterval(id);
  }, [timezone]);
  return t;
}

const METHOD_LABELS: Record<CalendarMethod, string> = {
  MOON_SIGHTING: 'Moon Sighting',
  CALCULATION: 'Calculation',
  BOTH: 'Both (Sighting + Calculation)',
};

const LANG_COLORS: Record<string, string> = {
  en: '#3b82f6', fr: '#8b5cf6', ar: '#10b981', ur: '#f59e0b',
  id: '#ef4444', tr: '#6366f1', ms: '#14b8a6', other: '#94a3b8',
};

const LANGUAGES = ['en', 'ar', 'fr', 'ur', 'id', 'tr', 'ms', 'other'];

const SCRAPE_TYPES: { value: ScrapeType; label: string }[] = [
  { value: 'html', label: 'HTML' },
  { value: 'rss', label: 'RSS' },
  { value: 'google_news', label: 'GNews' },
];

const SCRAPE_TYPE_COLORS: Record<ScrapeType, string> = {
  html: '#6366f1',
  rss: '#f59e0b',
  google_news: '#10b981',
};

interface Props {
  location: AdminLocationInfo;
  onClose: () => void;
  onSaved: (updated: AdminLocationInfo) => void;
}

export default function CountryDrawer({ location, onClose, onSaved }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);

  // Form state — mirrors location prop
  const [timezone, setTimezone] = useState(location.timezone || '');
  const [method, setMethod] = useState<CalendarMethod>(location.method || 'MOON_SIGHTING');
  const [authorityEN, setAuthorityEN] = useState(location.authorityEN || location.authority || '');
  const [authorityFR, setAuthorityFR] = useState(location.authorityFR || '');
  const [authorityAR, setAuthorityAR] = useState(location.authorityAR || '');
  const [additionalAuthorities, setAdditionalAuthorities] = useState<AdditionalAuthority[]>(location.additionalAuthorities || []);
  const [resources, setResources] = useState<LocationResource[]>(location.resources || []);
  const [notes, setNotes] = useState(location.notes || '');

  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState('');

  const localTime = useLocalTime(timezone || location.timezone);
  const canEnable = timezone.trim() && authorityEN.trim() && method && resources.filter(r => r.active).length > 0;

  function resetForm() {
    setTimezone(location.timezone || '');
    setMethod(location.method || 'MOON_SIGHTING');
    setAuthorityEN(location.authorityEN || location.authority || '');
    setAuthorityFR(location.authorityFR || '');
    setAuthorityAR(location.authorityAR || '');
    setAdditionalAuthorities(location.additionalAuthorities || []);
    setResources(location.resources || []);
    setNotes(location.notes || '');
    setError('');
  }

  function handleCancel() {
    resetForm();
    setEditing(false);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const result = await updateLocation(location.code, {
        timezone: timezone.trim(),
        method,
        authorityEN: authorityEN.trim(),
        authorityFR: authorityFR.trim(),
        authorityAR: authorityAR.trim(),
        additionalAuthorities,
        resources,
        notes: notes.trim(),
      });
      onSaved(result.location);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleEnabled() {
    setToggling(true);
    setError('');
    try {
      const result = await updateLocation(location.code, { enabled: !location.enabled });
      onSaved(result.location);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setToggling(false);
    }
  }

  // Additional authorities
  function addAuthority() { setAdditionalAuthorities(p => [...p, { name: '', description: '' }]); }
  function updateAuthority(i: number, f: keyof AdditionalAuthority, v: string) {
    setAdditionalAuthorities(p => p.map((a, idx) => idx === i ? { ...a, [f]: v } : a));
  }
  function removeAuthority(i: number) { setAdditionalAuthorities(p => p.filter((_, idx) => idx !== i)); }

  // Resources
  function addResource() { setResources(p => [...p, { url: '', language: 'en', comment: '', active: true, scrapeType: 'html' as ScrapeType }]); }
  function updateResource(i: number, f: keyof LocationResource, v: string | boolean) {
    setResources(p => p.map((r, idx) => idx === i ? { ...r, [f]: v } : r));
  }
  function removeResource(i: number) { setResources(p => p.filter((_, idx) => idx !== i)); }

  const displayMethod = location.method;
  const displayResources = location.resources || [];
  const displayAdditional = location.additionalAuthorities || [];

  return (
    <div className={styles.overlay} ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }}>
      <div className={styles.drawer}>

        {/* Header */}
        <div className={styles.drawerHeader}>
          <div className={styles.drawerTitle}>
            <span className={styles.drawerFlag}>{flagEmoji(location.code)}</span>
            <div>
              <h2 className={styles.drawerName}>{location.name}</h2>
              <p className={styles.drawerRegion}>{location.region || location.code}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            {!editing && (
              <button className={styles.editBtn} onClick={() => setEditing(true)}>Edit</button>
            )}
            <button className={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        <div className={styles.drawerBody}>
          {/* Status row */}
          <div className={styles.statusRow}>
            <span className={`${styles.statusChip} ${location.enabled ? styles.chipEnabled : styles.chipDisabled}`}>
              {location.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <span className={styles.localTime}>{localTime} local</span>
          </div>

          {error && <div className={styles.errorBanner}>{error}</div>}

          {editing ? (
            /* ── EDIT MODE ──────────────────────────── */
            <>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>General Information</h3>
                <div className={styles.fieldGrid}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>Country Code</label>
                    <span className={styles.readOnly}>{location.code}</span>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>Timezone</label>
                    <input className={styles.input} value={timezone} onChange={e => setTimezone(e.target.value)} placeholder="e.g. Africa/Casablanca" />
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Announcement Method <span className={styles.required}>*</span></h3>
                <div className={styles.methodGrid}>
                  {(Object.keys(METHOD_LABELS) as CalendarMethod[]).map(m => (
                    <button key={m} className={`${styles.methodBtn} ${method === m ? styles.methodActive : ''}`} onClick={() => setMethod(m)}>
                      {METHOD_LABELS[m]}
                    </button>
                  ))}
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Main Official Authority <span className={styles.required}>*</span></h3>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Name (English) <span className={styles.required}>*</span></label>
                  <input className={styles.input} value={authorityEN} onChange={e => setAuthorityEN(e.target.value)} placeholder="e.g. Ministry of Islamic Affairs" />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Name (French)</label>
                  <input className={styles.input} value={authorityFR} onChange={e => setAuthorityFR(e.target.value)} placeholder="Nom en français" />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} dir="rtl">الاسم بالعربية</label>
                  <input className={`${styles.input} ${styles.rtl}`} value={authorityAR} onChange={e => setAuthorityAR(e.target.value)} placeholder="مثلاً: وزارة الشؤون الإسلامية" dir="rtl" />
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>Additional Authorities</h3>
                  <button className={styles.addBtn} onClick={addAuthority}>+ Add</button>
                </div>
                {additionalAuthorities.length === 0 && <p className={styles.emptyHint}>No additional authorities.</p>}
                {additionalAuthorities.map((auth, idx) => (
                  <div key={idx} className={styles.authorityRow}>
                    <div className={styles.authorityFields}>
                      <input className={styles.input} value={auth.name} onChange={e => updateAuthority(idx, 'name', e.target.value)} placeholder="Authority name" />
                      <input className={styles.input} value={auth.description || ''} onChange={e => updateAuthority(idx, 'description', e.target.value)} placeholder="Type / description (optional)" />
                    </div>
                    <button className={styles.removeBtn} onClick={() => removeAuthority(idx)}>✕</button>
                  </div>
                ))}
              </section>

              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>Sources & Resources <span className={styles.required}>*</span></h3>
                  <button className={styles.addBtn} onClick={addResource}>+ Add</button>
                </div>
                {resources.length === 0 && <p className={styles.emptyHint}>At least one active resource is required to enable.</p>}
                {resources.map((res, idx) => (
                  <div key={idx} className={`${styles.resourceRow} ${!res.active ? styles.resourceInactive : ''}`}>
                    <div className={styles.resourceFields}>
                      <input className={styles.input} type="url" value={res.url} onChange={e => updateResource(idx, 'url', e.target.value)} placeholder="https://example.com/source" />
                      <div className={styles.resourceMeta}>
                        <select className={styles.select} value={res.language} onChange={e => updateResource(idx, 'language', e.target.value)}>
                          {LANGUAGES.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                        </select>
                        <div className={styles.scrapeTypeGroup}>
                          {SCRAPE_TYPES.map(st => (
                            <button
                              key={st.value}
                              className={`${styles.scrapeTypeBtn} ${(res.scrapeType ?? 'html') === st.value ? styles.scrapeTypeActive : ''}`}
                              style={(res.scrapeType ?? 'html') === st.value ? { background: SCRAPE_TYPE_COLORS[st.value] + '20', color: SCRAPE_TYPE_COLORS[st.value], borderColor: SCRAPE_TYPE_COLORS[st.value] } : {}}
                              onClick={() => updateResource(idx, 'scrapeType', st.value)}
                              type="button"
                            >
                              {st.label}
                            </button>
                          ))}
                        </div>
                        <input className={styles.input} value={res.comment || ''} onChange={e => updateResource(idx, 'comment', e.target.value)} placeholder="Label / comment (optional)" />
                        <button className={`${styles.activeToggle} ${res.active ? styles.activeOn : styles.activeOff}`} onClick={() => updateResource(idx, 'active', !res.active)}>
                          {res.active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>
                    <button className={styles.removeBtn} onClick={() => removeResource(idx)}>✕</button>
                  </div>
                ))}
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Operational Notes</h3>
                <textarea className={styles.textarea} value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="e.g. Authority usually announces late. Follows Saudi Arabia frequently." />
              </section>
            </>
          ) : (
            /* ── READ MODE ──────────────────────────── */
            <>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>General Information</h3>
                <div className={styles.readGrid}>
                  <div className={styles.readRow}>
                    <span className={styles.readLabel}>Code</span>
                    <span className={styles.readValue}>{location.code}</span>
                  </div>
                  <div className={styles.readRow}>
                    <span className={styles.readLabel}>Timezone</span>
                    <span className={styles.readValue}>{location.timezone || <span className={styles.noData}>—</span>}</span>
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Announcement Method</h3>
                {displayMethod
                  ? <span className={`${styles.methodChip} ${styles[`chip_${displayMethod}`]}`}>{METHOD_LABELS[displayMethod] || displayMethod}</span>
                  : <span className={styles.noData}>—</span>
                }
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Main Official Authority</h3>
                {(location.authorityEN || location.authority) ? (
                  <div className={styles.authorityBlock}>
                    {(location.authorityEN || location.authority) && (
                      <div className={styles.authorityReadRow}>
                        <span className={styles.langTag} style={{ background: LANG_COLORS.en + '18', color: LANG_COLORS.en }}>EN</span>
                        <span className={styles.readValue}>{location.authorityEN || location.authority}</span>
                      </div>
                    )}
                    {location.authorityFR && (
                      <div className={styles.authorityReadRow}>
                        <span className={styles.langTag} style={{ background: LANG_COLORS.fr + '18', color: LANG_COLORS.fr }}>FR</span>
                        <span className={styles.readValue}>{location.authorityFR}</span>
                      </div>
                    )}
                    {location.authorityAR && (
                      <div className={styles.authorityReadRow}>
                        <span className={styles.langTag} style={{ background: LANG_COLORS.ar + '18', color: LANG_COLORS.ar }}>AR</span>
                        <span className={`${styles.readValue} ${styles.rtlText}`} dir="rtl">{location.authorityAR}</span>
                      </div>
                    )}
                  </div>
                ) : <span className={styles.noData}>—</span>}
              </section>

              {displayAdditional.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Additional Authorities</h3>
                  <div className={styles.additionalList}>
                    {displayAdditional.map((auth, i) => (
                      <div key={i} className={styles.additionalItem}>
                        <span className={styles.additionalName}>{auth.name}</span>
                        {auth.description && <span className={styles.additionalDesc}>{auth.description}</span>}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Sources & Resources</h3>
                {displayResources.length === 0
                  ? <span className={styles.noData}>No sources configured.</span>
                  : (
                    <div className={styles.sourceList}>
                      {displayResources.map((res, i) => (
                        <a
                          key={i}
                          href={res.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${styles.sourceItem} ${!res.active ? styles.sourceInactive : ''}`}
                        >
                          <span
                            className={styles.sourceLang}
                            style={{ background: (LANG_COLORS[res.language] || LANG_COLORS.other) + '18', color: LANG_COLORS[res.language] || LANG_COLORS.other }}
                          >
                            {res.language.toUpperCase()}
                          </span>
                          {res.scrapeType && (
                            <span
                              className={styles.scrapeTypeBadge}
                              style={{ background: SCRAPE_TYPE_COLORS[res.scrapeType] + '18', color: SCRAPE_TYPE_COLORS[res.scrapeType] }}
                            >
                              {SCRAPE_TYPES.find(s => s.value === res.scrapeType)?.label ?? res.scrapeType}
                            </span>
                          )}
                          <div className={styles.sourceContent}>
                            <span className={styles.sourceLabel}>{res.comment || res.url}</span>
                            <span className={styles.sourceUrl}>{res.url.replace(/^https?:\/\//, '')}</span>
                          </div>
                          <svg className={styles.sourceArrow} viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                            <path d="M6.75 1.75a.75.75 0 000 1.5H11v5.25a.75.75 0 001.5 0v-6a.75.75 0 00-.75-.75h-5zM4.56 4.56a.75.75 0 011.06 0L12 10.94V8a.75.75 0 011.5 0v4.25a.75.75 0 01-.75.75H8.5a.75.75 0 010-1.5h2.94L5.56 5.62a.75.75 0 010-1.06z" />
                          </svg>
                        </a>
                      ))}
                    </div>
                  )
                }
              </section>

              {location.notes && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Operational Notes</h3>
                  <p className={styles.noteText}>{location.notes}</p>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.drawerFooter}>
          <button
            className={`${styles.enableBtn} ${location.enabled ? styles.enableBtnDisable : styles.enableBtnEnable}`}
            onClick={handleToggleEnabled}
            disabled={toggling || (!location.enabled && !canEnable)}
            title={!canEnable && !location.enabled ? 'Fill timezone, authority, method, and at least one resource first' : undefined}
          >
            {toggling ? '…' : location.enabled ? 'Disable' : 'Enable'}
          </button>

          {editing ? (
            <div className={styles.editActions}>
              <button className={styles.cancelBtn} onClick={handleCancel}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          ) : (
            <button className={styles.editBtnFooter} onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
